/**
 * Ownership Section
 * PROJ-045 - Node Properties Panel Enhancement
 *
 * Section for assigning owner and departments to workflow nodes.
 * - Owner: Single user selection via combobox
 * - Departments: Multi-select with tag display
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Users, User, Building2, X, Loader2 } from 'lucide-react';
import { CollapsibleSection } from '@/components/ui/collapsible-section';
import { EntityLookup, type EntityOption } from '@/components/ui/entity-lookup';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { listUsers } from '@/lib/userApi';
import { listDepartments } from '@/lib/departmentsApi';
import { useDebounce } from '@/hooks/use-debounce';

// Types for owner and department
export interface OwnerInfo {
  id: string;
  fullName: string;
  email: string;
  avatarUrl?: string;
}

export interface DepartmentInfo {
  id: string;
  code: string;
  name: string;
}

interface OwnershipSectionProps {
  nodeId: string;
  // Owner (single user)
  ownerId?: string;
  owner?: OwnerInfo;
  onOwnerChange: (owner: OwnerInfo | null) => void;
  // Departments (multiple)
  departmentIds?: string[];
  departments?: DepartmentInfo[];
  onDepartmentsChange: (departments: DepartmentInfo[]) => void;
}

// Stable empty array to prevent infinite re-renders from default parameter
const EMPTY_DEPARTMENTS: DepartmentInfo[] = [];
const EMPTY_DEPARTMENT_IDS: string[] = [];

export function OwnershipSection({
  nodeId,
  ownerId,
  owner,
  onOwnerChange,
  departmentIds,
  departments,
  onDepartmentsChange
}: OwnershipSectionProps) {
  // Use stable empty arrays as defaults
  const stableDepartmentIds = departmentIds ?? EMPTY_DEPARTMENT_IDS;
  const stableDepartments = departments ?? EMPTY_DEPARTMENTS;

  // State for department selector
  const [deptOpen, setDeptOpen] = useState(false);
  const [deptSearch, setDeptSearch] = useState('');
  const [deptOptions, setDeptOptions] = useState<DepartmentInfo[]>([]);
  const [deptLoading, setDeptLoading] = useState(false);
  const [allDepartments, setAllDepartments] = useState<DepartmentInfo[]>([]); // Cache all departments for fallback
  const [showingFallback, setShowingFallback] = useState(false); // Track if showing fallback results

  // Local state for displayed departments (handles hydration from IDs)
  const [localDepartments, setLocalDepartments] = useState<DepartmentInfo[]>(stableDepartments);

  // Track previous departments to avoid unnecessary updates
  const prevDepartmentsRef = useRef<DepartmentInfo[]>(stableDepartments);

  const debouncedDeptSearch = useDebounce(deptSearch, 300);

  // Memoize department IDs string for stable dependency
  const departmentIdsKey = useMemo(() => stableDepartmentIds.join(','), [stableDepartmentIds]);

  // Sync localDepartments when departments prop changes (only if content actually changed)
  useEffect(() => {
    // Compare using sorted ID strings
    const prevIds = prevDepartmentsRef.current.map(d => d.id).sort().join(',');
    const newIds = stableDepartments.map(d => d.id).sort().join(',');

    if (prevIds !== newIds) {
      prevDepartmentsRef.current = stableDepartments;
      setLocalDepartments(stableDepartments);
    }
  }, [stableDepartments]);

  // Hydrate departments from IDs if we have IDs but no department objects
  useEffect(() => {
    // Only hydrate if we have IDs but local state is empty
    if (stableDepartmentIds.length > 0 && localDepartments.length === 0) {
      const hydrateFromIds = async () => {
        try {
          const response = await listDepartments({ limit: 100 });
          const hydrated = response.data
            .filter((d) => stableDepartmentIds.includes(d.id))
            .map((d) => ({ id: d.id, code: d.code, name: d.name }));
          if (hydrated.length > 0) {
            setLocalDepartments(hydrated);
          }
        } catch (error) {
          console.error('Error hydrating departments:', error);
        }
      };
      hydrateFromIds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentIdsKey]);

  // Search users for owner selector
  const searchUsers = useCallback(async (query: string): Promise<EntityOption[]> => {
    try {
      const response = await listUsers({
        search: query,
        limit: 10,
        status: 'ACTIVE'
      });

      return response.users.map((user) => ({
        value: user.id,
        label: user.fullName || user.username || user.email,
        subtitle: user.email,
        metadata: {
          id: user.id,
          fullName: user.fullName,
          email: user.email,
          avatarUrl: user.avatarUrl
        }
      }));
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  }, []);

  // Handle owner selection
  const handleOwnerSelect = useCallback((value: string | undefined) => {
    if (!value) {
      onOwnerChange(null);
      return;
    }

    // Need to find the selected user to get full info
    searchUsers('').then((users) => {
      const selected = users.find((u) => u.value === value);
      if (selected?.metadata) {
        onOwnerChange({
          id: selected.metadata.id,
          fullName: selected.metadata.fullName,
          email: selected.metadata.email,
          avatarUrl: selected.metadata.avatarUrl
        });
      }
    });
  }, [onOwnerChange, searchUsers]);

  // Search departments - fetches from server and falls back to showing all if no results
  const searchDepartments = useCallback(async (query: string) => {
    setDeptLoading(true);
    try {
      // Always fetch with search query for server-side filtering
      const response = await listDepartments({
        search: query,
        limit: 50
      });

      // Filter out already selected departments using local state
      const selectedIds = localDepartments.map((d) => d.id);
      const available = response.data.filter(
        (dept) => !selectedIds.includes(dept.id)
      );

      const mappedOptions = available.map((dept) => ({
        id: dept.id,
        code: dept.code,
        name: dept.name
      }));

      // If search has a query but no results, show all available departments as fallback
      if (query && mappedOptions.length === 0 && allDepartments.length > 0) {
        const fallbackOptions = allDepartments.filter(
          (dept) => !selectedIds.includes(dept.id)
        );
        setDeptOptions(fallbackOptions);
        setShowingFallback(true);
      } else {
        setDeptOptions(mappedOptions);
        setShowingFallback(false);
        // Cache all departments when loading without search query
        if (!query && mappedOptions.length > 0) {
          setAllDepartments(mappedOptions);
        }
      }
    } catch (error) {
      console.error('Error searching departments:', error);
      setDeptOptions([]);
    } finally {
      setDeptLoading(false);
    }
  }, [localDepartments, allDepartments]);

  // Load departments when opening or search changes
  const handleDeptOpen = useCallback((open: boolean) => {
    setDeptOpen(open);
    if (open) {
      searchDepartments('');
    } else {
      // Reset state when closing
      setDeptSearch('');
      setShowingFallback(false);
    }
  }, [searchDepartments]);

  // Effect for debounced search
  useEffect(() => {
    if (deptOpen && debouncedDeptSearch) {
      searchDepartments(debouncedDeptSearch);
    }
  }, [deptOpen, debouncedDeptSearch, searchDepartments]);

  // Add department
  const handleDeptAdd = useCallback((dept: DepartmentInfo) => {
    const alreadySelected = localDepartments.some((d) => d.id === dept.id);
    if (!alreadySelected) {
      const updated = [...localDepartments, dept];
      setLocalDepartments(updated);
      onDepartmentsChange(updated);
    }
    setDeptOpen(false);
    setDeptSearch('');
  }, [localDepartments, onDepartmentsChange]);

  // Remove department
  const handleDeptRemove = useCallback((deptId: string) => {
    const updated = localDepartments.filter((d) => d.id !== deptId);
    setLocalDepartments(updated);
    onDepartmentsChange(updated);
  }, [localDepartments, onDepartmentsChange]);

  // Custom render for user in EntityLookup
  const renderUserOption = useCallback((option: EntityOption) => (
    <div className="flex items-center gap-2">
      <Avatar className="h-6 w-6">
        <AvatarImage src={option.metadata?.avatarUrl} />
        <AvatarFallback className="text-xs">
          {option.label.charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-col">
        <span className="font-medium text-sm">{option.label}</span>
        <span className="text-xs text-muted-foreground">{option.subtitle}</span>
      </div>
    </div>
  ), []);

  return (
    <CollapsibleSection
      id={`${nodeId}-ownership`}
      title="Ownership"
      icon={Users}
      defaultOpen={false}
      badge={
        (owner || localDepartments.length > 0) ? (
          <Badge variant="secondary" className="ml-2 text-xs">
            {owner ? '1 Owner' : ''}{owner && localDepartments.length > 0 ? ', ' : ''}{localDepartments.length > 0 ? `${localDepartments.length} Dept${localDepartments.length > 1 ? 's' : ''}` : ''}
          </Badge>
        ) : null
      }
    >
      <div className="space-y-4">
        {/* Owner Selector */}
        <div className="space-y-2">
          <label className="text-xs font-medium flex items-center gap-1.5 text-muted-foreground">
            <User className="h-3 w-3" />
            Owner
          </label>

          {owner ? (
            <div className="flex items-center justify-between p-2 border rounded-md bg-muted/30">
              <div className="flex items-center gap-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={owner.avatarUrl} />
                  <AvatarFallback className="text-xs">
                    {owner.fullName?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{owner.fullName}</span>
                  <span className="text-xs text-muted-foreground">{owner.email}</span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => onOwnerChange(null)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <EntityLookup
              value={ownerId}
              onChange={handleOwnerSelect}
              onSearch={searchUsers}
              placeholder="Search user..."
              searchPlaceholder="Type to search users..."
              emptyMessage="No users found"
              renderOption={renderUserOption}
              minSearchLength={0}
            />
          )}
        </div>

        {/* Departments Multi-Select */}
        <div className="space-y-2">
          <label className="text-xs font-medium flex items-center gap-1.5 text-muted-foreground">
            <Building2 className="h-3 w-3" />
            Departments
          </label>

          {/* Selected departments as tags */}
          {localDepartments.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {localDepartments.map((dept) => (
                <Badge
                  key={dept.id}
                  variant="secondary"
                  className="gap-1 pr-1"
                >
                  <span>{dept.name}</span>
                  <button
                    type="button"
                    className="ml-1 hover:bg-muted rounded-full p-0.5"
                    onClick={() => handleDeptRemove(dept.id)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          {/* Department search/add */}
          <Popover open={deptOpen} onOpenChange={handleDeptOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start text-muted-foreground"
              >
                <Building2 className="h-4 w-4 mr-2" />
                Add departments...
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Search departments..."
                  value={deptSearch}
                  onValueChange={(value) => {
                    setDeptSearch(value);
                    searchDepartments(value);
                  }}
                />
                <CommandList>
                  {deptLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span className="text-sm text-muted-foreground">Searching...</span>
                    </div>
                  ) : deptOptions.length === 0 ? (
                    <CommandEmpty>No departments found</CommandEmpty>
                  ) : (
                    <CommandGroup heading={showingFallback ? `No match for "${deptSearch}" - showing all` : undefined}>
                      {deptOptions.map((dept) => (
                        <CommandItem
                          key={dept.id}
                          value={dept.id}
                          onSelect={() => handleDeptAdd(dept)}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{dept.name}</span>
                            <span className="text-xs text-muted-foreground">{dept.code}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          <p className="text-xs text-muted-foreground">
            Departments consuming this node
          </p>
        </div>
      </div>
    </CollapsibleSection>
  );
}
